import React, { useState, useContext, useEffect } from "react";
import Cookies from "js-cookie";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import UserToolbar from "../../components/users/UserToolbar";
import { toast } from "react-toastify";
import AppContext from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import {
  listCcWorkspaceUsers,
  setCcWorkspaceUserActive,
} from "../../service/CallCenterUserAdmin";
import { getCallCenterApiErrorMessage } from "../../service/CallCenterApi";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import { getPermissionByScreenCode } from "../../utils/permissions";

const SORT_BY_OPTIONS = [
  { value: "NAME", label: "Name" },
  { value: "EMAIL", label: "Email" },
  { value: "CREATED_AT", label: "Created" },
];

const SORT_DIR_OPTIONS = [
  { value: "ASC", label: "Ascending" },
  { value: "DESC", label: "Descending" },
];

const UserList = () => {
  const navigate = useNavigate();
  const { theme, langMode, userType } = useContext(AppContext);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("NAME");
  const [sortDir, setSortDir] = useState("ASC");
  const [userList, setUerList] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [listMeta, setListMeta] = useState({
    hasNext: undefined,
    hasPrevious: undefined,
    apiPageCount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [userStatusOptions] = useState([
    { label: translations[langMode].Enable, value: "Enable" },
    { label: translations[langMode].disable, value: "Disable" },
  ]);

  const userPerm = getPermissionByScreenCode("USER_MANAGEMENT");
  const canUpdateUser =
    userType === "super_admin" || Boolean(userPerm?.canUpdate);
  const showStatusControls =
    userType === "super_admin" || userType === "admin";
  const showActionColumn = canUpdateUser;

  const apiPageCount =
    typeof listMeta.apiPageCount === "number" && listMeta.apiPageCount > 0
      ? listMeta.apiPageCount
      : 0;
  const lastPageIndex =
    totalValue <= 0
      ? 0
      : apiPageCount > 0
        ? Math.max(0, apiPageCount - 1)
        : Math.max(0, Math.ceil(totalValue / pageSize) - 1);

  const canGoPrev = currentPage > 0;
  const canGoNext =
    listMeta.hasNext === false
      ? false
      : listMeta.hasNext === true
        ? true
        : currentPage < lastPageIndex;

  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const totalItems = totalValue;
  const isRtl = langMode === "ar";

  const editInfo = (userId) => {
    const updateUserUrl = `/add-user/${userId}?isEdit=true`;
    navigate(updateUserUrl);
  };

  const tableHeaders = [
    translations[langMode].userId,
    translations[langMode].Role,
    translations[langMode].username,
    translations[langMode].Email,
    translations[langMode].Phone,
    ...(showStatusControls ? [translations[langMode].Status] : []),
    ...(showActionColumn ? [translations[langMode].Action] : []),
  ];

  const handlePageSizeChange = (e) => {
    const raw = parseInt(e.target.value, 10);
    const size = Math.min(200, Math.max(1, Number.isFinite(raw) ? raw : 10));
    setPageSize(size);
    setCurrentPage(0);
  };

  const goToPreviousPage = () => {
    if (canGoPrev) setCurrentPage((p) => p - 1);
  };

  const goToNextPage = () => {
    if (canGoNext) setCurrentPage((p) => p + 1);
  };

  const fetchData = async (pageIndex, size, sortByParam, sortDirParam) => {
    try {
      if (!Cookies.get("accessToken")) {
        setUerList([]);
        setTotalValue(0);
        setListMeta({ hasNext: undefined, hasPrevious: undefined, apiPageCount: 0 });
        return;
      }
      setLoading(true);
      const data = await listCcWorkspaceUsers({
        page: pageIndex,
        size,
        sortBy: sortByParam,
        sortDir: sortDirParam,
      });
      setUerList(Array.isArray(data?.items) ? data.items : []);
      const totalElements =
        typeof data?.totalElements === "number" ? data.totalElements : 0;
      setTotalValue(totalElements);

      const apiTotalPages =
        typeof data?.totalPages === "number" ? data.totalPages : 0;
      setListMeta({
        hasNext: data?.hasNext,
        hasPrevious: data?.hasPrevious,
        apiPageCount: apiTotalPages,
      });
      setLoading(false);
    } catch (error) {
      const envelope = error?.envelope ?? error?.response?.data;
      const msg =
        envelope != null
          ? getCallCenterApiErrorMessage(envelope, translations[langMode].failedtofetchusers)
          : translations[langMode].failedtofetchusers;
      toast.error(msg);
      setUerList([]);
      setTotalValue(0);
      setListMeta({ hasNext: undefined, hasPrevious: undefined, apiPageCount: 0 });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, sortBy, sortDir);
  }, [currentPage, pageSize, sortBy, sortDir]);

  const isCurrentUserRow = (rowUserId) => {
    const cookieId = Cookies.get("userId");
    return cookieId != null && String(rowUserId) === String(cookieId);
  };

  const handleActionChange = async (selectedAction, rowUser) => {
    const updatedStatus = selectedAction === "Enable";
    if (!updatedStatus && isCurrentUserRow(rowUser.id)) {
      toast.error("You cannot deactivate your own account.");
      return;
    }
    try {
      await setCcWorkspaceUserActive(rowUser.id, updatedStatus);
      const updatedUserData = userList.map((user) =>
        user.id === rowUser.id ? { ...user, active: updatedStatus } : user
      );
      setUerList(updatedUserData);
      toast.success(translations[langMode].userStatusupdatesuc);
    } catch (error) {
      const envelope = error?.envelope ?? error?.response?.data;
      const msg =
        envelope != null
          ? getCallCenterApiErrorMessage(envelope, translations[langMode].failedtoupdateuserstatus)
          : translations[langMode].failedtoupdateuserstatus;
      toast.error(msg);
    }
  };

  return (
    <>
      <div
        className={` w-full h-full   ${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
          }`}
      >
        <h4
          className={`uppercase ml-5 inline-flex items-center gap-2 mt-5 font-semibold whitespace-nowrap text-gray-800 ${theme === "dark"
            ? "dark-mode "
            : theme === "high-contrast"
              ? "high-contrast  "
              : ""
            }`}
          data-testid="user-list-title"
        >
          {translations[langMode].userlist}
        </h4>
        <div
          className={`${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            } w-full sm:w-auto mt-3 px-4 bg-white  sm:rounded-lg`}
        >
          <UserToolbar />
          <div className="flex flex-wrap items-end gap-3 mt-4 pb-2 border-b border-gray-100">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Sort by</label>
              <select
                className="border border-gray-300 rounded-lg text-sm px-2 py-2 min-w-[140px] bg-white"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(0);
                }}
              >
                {SORT_BY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Direction</label>
              <select
                className="border border-gray-300 rounded-lg text-sm px-2 py-2 min-w-[120px] bg-white"
                value={sortDir}
                onChange={(e) => {
                  setSortDir(e.target.value);
                  setCurrentPage(0);
                }}
              >
                {SORT_DIR_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className=" flex justify-between mt-3">
            <p className="text-sm text-gray-400 flex items-center ">
              {`${startIndex + 1}-${Math.min(endIndex, totalItems)} of ${totalItems} ${translations[langMode].items}`}
              {isRtl ? (
                <IoIosArrowForward
                  onClick={goToPreviousPage}
                  disabled={!canGoPrev}
                  className={`cursor-pointer ${!canGoPrev ? "opacity-50" : ""}`}
                />
              ) : (
                <IoIosArrowBack
                  onClick={goToPreviousPage}
                  disabled={!canGoPrev}
                  className={`cursor-pointer ${!canGoPrev ? "opacity-50" : ""}`}
                />
              )}
              {isRtl ? (
                <IoIosArrowBack
                  onClick={goToNextPage}
                  disabled={!canGoNext}
                  className={`cursor-pointer ${!canGoNext ? "opacity-50" : ""}`}
                />
              ) : (
                <IoIosArrowForward
                  onClick={goToNextPage}
                  disabled={!canGoNext}
                  className={`cursor-pointer ${!canGoNext ? "opacity-50" : ""}`}
                />
              )}
            </p>

            <div className="text-sm text-gray-400 flex items-center gap-2">
              <label htmlFor="pageSize">{translations[langMode].rows}:</label>
              <select
                id="pageSize"
                className={`bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-24 p-2.5  dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 ${theme === "dark"
                  ? "dark-mode"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                  }`}
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto my-5">
            <div className="relative max-h-[450px] overflow-y-auto rounded-xl shadow-lg border border-gray-100">
              <table className="min-w-full text-sm text-left text-gray-700" data-testid="user-list-table">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
                  <tr>
                    <th className="w-2 p-2"></th>
                    {tableHeaders.map((header, index) => (
                      <th
                        key={index}
                        scope="col"
                        className="px-6 py-3 text-xs font-semibold tracking-wider uppercase text-gray-600"
                      >
                        {header}
                      </th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                {loading && <Spinner />}
                <tbody className="bg-white divide-y divide-gray-100">
                  {userList && userList.length > 0 ? (
                    userList.map((user) => {
                      const selfRow = isCurrentUserRow(user.id);
                      return (
                        <tr
                          key={user.id}
                          className="hover:bg-gray-50 transition-all duration-200 ease-in-out"
                        >
                          <td className="w-2 p-2"></td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-block w-2.5 h-2.5 rounded-full ${user.active ? "bg-green-500" : "bg-red-500"
                                  }`}
                                title={user.active ? "Active" : "Inactive"}
                              />
                              <span className="font-medium text-gray-900">{user.id}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-xs font-semibold px-3 py-1.5 rounded-lg border bg-blue-50 text-blue-700 border-blue-300">
                              {(user.role || "").replace(/_/g, " ")}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap font-medium">
                            {user.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                            {user.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                            {user.agentExtension || "—"}
                          </td>
                          {showStatusControls && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <select
                                title={
                                  selfRow && user.active
                                    ? "You cannot deactivate your own account"
                                    : undefined
                                }
                                className="border border-gray-300 text-sm px-3 py-1 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                                value={user.active ? "Enable" : "Disable"}
                                onChange={(e) => handleActionChange(e.target.value, user)}
                              >
                                {userStatusOptions.map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                    disabled={selfRow && user.active && option.value === "Disable"}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          )}
                          {showActionColumn && canUpdateUser && (
                            <td className="px-9 py-4 whitespace-nowrap">
                              <div className="relative group inline-block">
                                <button type="button" onClick={() => editInfo(user.id)}>
                                  <img
                                    src="https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344902/actionImage_orw2hy.png"
                                    alt="Edit user"
                                    className="cursor-pointer"
                                  />
                                </button>
                                <div
                                  role="tooltip"
                                  className="absolute top-1/2 -translate-y-1/2 right-full mr-2 z-10 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-300"
                                >
                                  <div className="relative bg-white text-gray-800 text-xs font-medium px-3 py-1.5 rounded-lg shadow-md whitespace-nowrap border border-gray-200">
                                    {translations[langMode].editandupdateuserdetail}
                                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white border-l border-t border-gray-200 rotate-45"></div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={tableHeaders.length + 2}
                        className="text-center py-6 text-gray-500"
                        data-testid="no-users-found-text"
                      >
                        {translations[langMode].nowuserfound}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default UserList;
